// Commitment Tracker chip strip — Polish chunk.
//
// FE-041 packed search + status/priority selects + Overdue/Blocked/
// Unassigned toggles + the chip strip into one component. The polish
// chunk pulled the controls up to the page header (search becomes
// its own input, presets live in ``CommitmentFilterDropdown``,
// status / priority filters live inside the dropdown), so this
// component now does one thing: render the "Filtered by:" chip
// strip beneath the controls.

import { X } from "lucide-react";

import type {
  CommitmentListFilters,
  CommitmentStatus,
} from "./types";


interface CommitmentFiltersBarProps {
  filters: CommitmentListFilters;
  onChange: (next: CommitmentListFilters) => void;
}


export function CommitmentFiltersBar({
  filters, onChange,
}: CommitmentFiltersBarProps) {
  const chips = buildChips(filters);
  if (chips.length === 0) {
    // The chip strip is mounted only when there's something to show.
    // FE-041 / FE-042 tests assert on the wrapper testid below
    // when filters are active; in the empty case the page renders
    // nothing so the board / list don't get pushed down.
    return null;
  }
  return (
    <div data-testid="commitments-filters-bar" className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
          Filtered by:
        </span>
        <ul
          data-testid="commitments-chips"
          role="list"
          className="flex flex-wrap items-center gap-2"
        >
          {chips.map((chip) => (
            <li key={chip.key}>
              <FilterChip
                chipKey={chip.key}
                label={chip.label}
                onRemove={() => onChange(removeChip(filters, chip.key))}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}


type ChipKey =
  | "search" | "client_id" | "assigned_to" | "overdue" | "is_blocked"
  | "has_assignee" | "has_due_date_time" | "on_time"
  | "status" | "priority" | "category" | "snapshot_date"
  | "include_cancelled";


interface ChipDescriptor {
  key: ChipKey;
  label: string;
}


function buildChips(filters: CommitmentListFilters): ChipDescriptor[] {
  const chips: ChipDescriptor[] = [];
  if (filters.search) {
    chips.push({ key: "search", label: `Search: "${filters.search}"` });
  }
  if (filters.client_id) {
    chips.push({ key: "client_id", label: "Account filter" });
  }
  if (filters.assigned_to) {
    chips.push({ key: "assigned_to", label: "Assignee filter" });
  }
  if (filters.overdue === true) {
    chips.push({ key: "overdue", label: "Overdue" });
  }
  if (filters.is_blocked === true) {
    chips.push({ key: "is_blocked", label: "Blocked" });
  }
  if (filters.has_assignee === false) {
    chips.push({ key: "has_assignee", label: "Unassigned" });
  }
  if (filters.has_due_date_time === true) {
    chips.push({ key: "has_due_date_time", label: "Has due date" });
  }
  if (filters.has_due_date_time === false) {
    chips.push({ key: "has_due_date_time", label: "No due date" });
  }
  if (filters.on_time === false) {
    chips.push({ key: "on_time", label: "Late" });
  }
  if (filters.on_time === true) {
    chips.push({ key: "on_time", label: "On time" });
  }
  if (filters.status) {
    chips.push({
      key: "status",
      label: `Status: ${statusLabelText(filters.status)}`,
    });
  }
  if (filters.priority) {
    chips.push({
      key: "priority",
      label: `Priority: ${capitalise(filters.priority)}`,
    });
  }
  if (filters.category) {
    chips.push({
      key: "category",
      label: `Category: ${filters.category}`,
    });
  }
  if (filters.snapshot_date) {
    chips.push({
      key: "snapshot_date",
      label: `Snapshot ${formatDate(filters.snapshot_date)}`,
    });
  }
  if (filters.include_cancelled === true) {
    chips.push({ key: "include_cancelled", label: "Show cancelled" });
  }
  return chips;
}


function removeChip(
  filters: CommitmentListFilters, key: ChipKey,
): CommitmentListFilters {
  const next = { ...filters };
  delete next[key];
  return next;
}


function FilterChip({
  chipKey, label, onRemove,
}: { chipKey: ChipKey; label: string; onRemove: () => void }) {
  return (
    <span
      data-testid={`commitments-chip-${chipKey}`}
      className="inline-flex items-center gap-1.5 rounded-full bg-recessed px-2.5 py-1 text-xs text-text-primary"
    >
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label} filter`}
        data-testid={`commitments-chip-remove-${chipKey}`}
        className="text-text-tertiary transition-fast ease-out hover:text-text-primary"
      >
        <X size={12} />
      </button>
    </span>
  );
}


function statusLabelText(status: CommitmentStatus): string {
  switch (status) {
    case "backlog": return "Backlog";
    case "ready_for_work": return "Ready for work";
    case "in_progress": return "In progress";
    case "in_review": return "In review";
    case "done": return "Done";
    case "cancelled": return "Cancelled";
  }
}


function capitalise(value: string): string {
  return value.length === 0 ? value : value[0].toUpperCase() + value.slice(1);
}


function formatDate(value: string): string {
  const d = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric", timeZone: "UTC",
  });
}
